import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ProfinderQuery } from '@engine/models/admin'

import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import { ScacTypeahead } from '#components/ScacTypeahead.js'
import {
  getRangeOptions,
  RANGE_MINUTES,
  type RangeKey,
} from '#utils/timeRanges'

interface LocalFiltersState {
  externalId: string
  scac: string
  trace: string
  range: RangeKey
  customFrom: string
  customTo: string
}

export const useUnmappedTrackEventsFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [localFilters, setLocalFilters] = useState<LocalFiltersState>({
    externalId: searchParams.get('externalId') || '',
    scac: searchParams.get('scac') || '',
    trace: searchParams.get('trace') || '',
    range: (searchParams.get('range') as RangeKey) || 'all',
    customFrom: searchParams.get('customFrom') || '',
    customTo: searchParams.get('customTo') || '',
  })

  // Convert local datetime strings to UTC ISO format for API
  const filters = useMemo<ProfinderQuery>(() => {
    const now = new Date()
    let from: string | undefined
    let to: string | undefined

    if (localFilters.range === 'all') {
      // No date filters for 'all'
      from = undefined
      to = undefined
    } else if (localFilters.range === 'custom') {
      from = localFilters.customFrom
        ? new Date(localFilters.customFrom).toISOString()
        : undefined
      to = localFilters.customTo
        ? new Date(localFilters.customTo).toISOString()
        : undefined
    } else {
      const minutes = RANGE_MINUTES[localFilters.range]
      const fromDate = new Date(now.getTime() - minutes * 60 * 1_000)
      from = fromDate.toISOString()
      to = now.toISOString()
    }

    return {
      externalId: localFilters.externalId || undefined,
      carrier: localFilters.scac || undefined,
      trace: localFilters.trace || undefined,
      from,
      to,
    }
  }, [localFilters])

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (localFilters.externalId)
      params.set('externalId', localFilters.externalId)
    if (localFilters.scac) params.set('scac', localFilters.scac)
    if (localFilters.trace) params.set('trace', localFilters.trace)
    if (localFilters.range) params.set('range', localFilters.range)
    if (localFilters.customFrom)
      params.set('customFrom', localFilters.customFrom)
    if (localFilters.customTo) params.set('customTo', localFilters.customTo)
    setSearchParams(params, { replace: true })
  }, [localFilters, setSearchParams])

  return { filters, localFilters, setLocalFilters }
}

interface UnmappedTrackEventsFiltersProps {
  isLoading: boolean
  onRefresh: () => void
  localFilters: LocalFiltersState
  setLocalFilters: (filters: LocalFiltersState) => void
}

export const UnmappedTrackEventsFilters = ({
  isLoading,
  onRefresh,
  localFilters,
  setLocalFilters,
}: UnmappedTrackEventsFiltersProps) => {
  return (
    <AdminFilters isLoading={isLoading} onRefresh={onRefresh}>
      <FilterRow>
        <FilterInput
          label="External ID"
          value={localFilters.externalId}
          onChange={value =>
            setLocalFilters({ ...localFilters, externalId: value })
          }
          placeholder="Search by external ID..."
        />
        <ScacTypeahead
          value={localFilters.scac}
          onChange={value => setLocalFilters({ ...localFilters, scac: value })}
        />
        <FilterInput
          label="Trace ID"
          value={localFilters.trace}
          onChange={value => setLocalFilters({ ...localFilters, trace: value })}
          placeholder="Search by trace ID..."
        />
      </FilterRow>
      <FilterRow>
        <FilterSelect
          label="Time range"
          value={localFilters.range}
          onChange={value =>
            setLocalFilters({ ...localFilters, range: value as RangeKey })
          }
          options={getRangeOptions()}
        />
        {localFilters.range === 'custom' ? (
          <>
            <FilterInput
              label="From"
              value={localFilters.customFrom}
              onChange={value =>
                setLocalFilters({ ...localFilters, customFrom: value })
              }
              type="datetime-local"
            />
            <FilterInput
              label="To"
              value={localFilters.customTo}
              onChange={value =>
                setLocalFilters({ ...localFilters, customTo: value })
              }
              type="datetime-local"
            />
          </>
        ) : null}
      </FilterRow>
    </AdminFilters>
  )
}
